import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { SimpleEditor } from './tiptap-templates/simple/simple-editor';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { marked } from 'marked';

// Configure marked to support GFM tables
marked.setOptions({
  gfm: true,
  breaks: false,
});

const TiptapEditor = React.forwardRef(({ content, onSave, onCancel }, ref) => {
  const turndown = useMemo(() => {
    const service = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      bulletListMarker: '-',
    });
    // Enable GFM rules including tables
    service.use(gfm);

    // Keep line breaks in paragraphs inside table cells as <br>
    service.addRule('unwrapParagraphsInCells', {
      filter: (node) =>
        (node.nodeName === 'P' && node.parentElement && (node.parentElement.nodeName === 'TD' || node.parentElement.nodeName === 'TH')),
      replacement: (content) => content.trim()
    });

    // Ensure images (including data URLs) are preserved when saving.
    // When image has explicit dimensions (from resize), use HTML <img> tag
    // so that width survives the markdown → HTML → editor roundtrip.
    service.addRule('image', {
      filter: 'img',
      replacement: (_content, node) => {
        const alt = node.getAttribute('alt') || '';
        const src = node.getAttribute('src') || '';
        if (!src) return '';

        const width = node.getAttribute('width');
        const align = node.getAttribute('data-align');
        if (width || align) {
          const widthAttr = width ? ` width="${width}"` : '';
          const alignAttr = align ? ` data-align="${align}"` : '';
          // HTML img tag preserves dimensions/alignment through marked.parse() and TipTap reload
          return `<img src="${src}" alt="${alt}"${widthAttr}${alignAttr} style="max-width:100%;height:auto">`;
        }
        // Standard markdown image when no resize has been applied
        return `![${alt}](${src})`;
      },
    });

    return service;
  }, []);

  const sanitizeHtmlForMarkdown = useCallback((html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Remove colgroups and style attributes that don't serialize well to MD
      doc.querySelectorAll('colgroup, col').forEach((el) => el.remove());
      doc.querySelectorAll('table, thead, tbody, tfoot, tr, td, th').forEach((el) => {
        el.removeAttribute('style');
        el.removeAttribute('rowspan');
        el.removeAttribute('colspan');
      });

      // Unwrap <p> inside table cells to plain inline content
      doc.querySelectorAll('td > p, th > p').forEach((p) => {
        const parent = p.parentElement;
        if (!parent) return;
        // replace <p> with its children (preserving inline breaks)
        const frag = doc.createDocumentFragment();
        while (p.firstChild) frag.appendChild(p.firstChild);
        parent.replaceChild(frag, p);
      });

      return doc.body.innerHTML;
    } catch (e) {
      console.error('sanitizeHtmlForMarkdown error:', e);
      return html;
    }
  }, []);
  
  const simpleEditorRef = useRef(null);
  const containerRef = useRef(null);
  const lastSavedContentRef = useRef(content || '');
  // Track whether the component is still mounted to prevent stale blur callbacks
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Convert markdown to HTML, handling tables properly
  const markdownToHTML = useCallback((markdown) => {
    if (!markdown || typeof markdown !== 'string') return '';
    try {
      // Use marked to convert markdown (including tables) to HTML
      return marked.parse(markdown);
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      return markdown;
    }
  }, []);

  const getMarkdown = useCallback(() => {
    const editor = simpleEditorRef.current?.editor;
    if (editor && !editor.isDestroyed) {
      const html = editor.getHTML();
      const cleaned = sanitizeHtmlForMarkdown(html);
      let markdown = turndown.turndown(cleaned);
      // Normalize multiple blank lines and trim
      markdown = markdown.replace(/\n{3,}/g, '\n\n').trim();
      return markdown;
    }
    // Editor is gone — return null to signal "no live content available"
    return null;
  }, [turndown, sanitizeHtmlForMarkdown]);

  React.useImperativeHandle(ref, () => ({
    getMarkdown: () => getMarkdown() ?? content ?? '',
    getContent: () => getMarkdown() ?? content ?? '',
    save: () => {
      const md = getMarkdown() ?? content ?? '';
      if (onSave) onSave(md);
      lastSavedContentRef.current = md;
    },
  }), [getMarkdown, onSave, content]);

  // Handle blur - auto-save when clicking away and close editor
  const handleBlur = useCallback(() => {
    // Don't do anything if the component has already unmounted.
    // This prevents stale blur callbacks from overwriting a save
    // that was already committed by the explicit Save button.
    if (!isMountedRef.current) return;

    const currentMarkdown = getMarkdown();

    // If the editor is destroyed / unavailable, don't overwrite —
    // the explicit Save button already handled it.
    if (currentMarkdown === null) return;

    // Normalize both strings for comparison (trim whitespace)
    const normalizedCurrent = currentMarkdown.trim();
    const normalizedLast = lastSavedContentRef.current.trim();
    
    // Save if content changed
    if (normalizedCurrent !== normalizedLast && onSave) {
      onSave(currentMarkdown);
      lastSavedContentRef.current = currentMarkdown;
    }
    
    // Always close the editor when clicking outside (even if no changes)
    // Use a small delay to ensure save completes first
    setTimeout(() => {
      if (isMountedRef.current && onCancel) {
        onCancel();
      }
    }, 150);
  }, [getMarkdown, onSave, onCancel]);

  // Update last saved content when content prop changes (e.g., when switching sections)
  // This ensures we track the initial content correctly
  React.useEffect(() => {
    if (content !== undefined) {
      // Normalize the content the same way getMarkdown does
      const normalizedContent = typeof content === 'string' ? content.trim() : '';
      lastSavedContentRef.current = normalizedContent;
    }
  }, [content]);

  const htmlContent = useMemo(() => {
    if (!content) return '';
    if (typeof content === 'string') {
      return markdownToHTML(content);
    }
    return content;
  }, [content, markdownToHTML]);

  return (
    <div ref={containerRef}>
      <SimpleEditor
        ref={simpleEditorRef}
        content={htmlContent}
        onBlur={handleBlur}
      />
    </div>
  );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor;

